import { isUserFunder, isUserRecommender } from "@engine/round/accessors";
import type { RoundAggregate } from "@engine/round/aggregate";
import type { UserProfiles } from "@lib/types";
import { sleep } from "@lib/utils/sleep";
import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  type AppMode,
  clearGlobalImpersonation,
  getGlobalImpersonation,
  impersonationRef,
  setGlobalImpersonation,
  notifyImpersonationListeners,
  useImpersonationRefValue,
} from "./ImperativeState";
import { useCurrentUser } from "./hooks/use-current-user";
import { trpc } from "./trpc";
import userIsAdmin from "./utils/userIsAdmin";

export { RECENT_IMPERSONATE_PREFIX } from "@client/impersonationRef";
import {
  clearAllImpersonationLocalState,
  GLOBAL_RECENT_KEY,
  RECENT_IMPERSONATE_PREFIX,
  useActiveGlobalImpersonation,
} from "@client/impersonationRef";
const MAX_RECENT = 10;

export function readRecentUserIds(roundId: string): string[] {
  try {
    const raw = localStorage.getItem(`${RECENT_IMPERSONATE_PREFIX}${roundId}`);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function readGlobalRecentUserIds(): string[] {
  try {
    const raw = localStorage.getItem(GLOBAL_RECENT_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function trackGlobalRecentUser(userId: string) {
  try {
    const existing = readGlobalRecentUserIds();
    const updated = [userId, ...existing.filter((id) => id !== userId)].slice(
      0,
      MAX_RECENT,
    );
    localStorage.setItem(GLOBAL_RECENT_KEY, JSON.stringify(updated));
  } catch {
    // localStorage errors are non-critical
  }
}

/**
 * Remove every piece of impersonation state this device holds: the persisted
 * global impersonation, the in-memory ref, per-round app modes, and the
 * recent-target lists. Called on sign-out so the next session — possibly a
 * different user on a shared device — can't resume impersonating or see a
 * stale banner.
 *
 * Deliberately does NOT notify impersonation listeners: the route subtree is
 * keyed on the impersonation ref (QueueBoundary in App.tsx), so notifying
 * mid-sign-out would remount the SignOut page while its async flow is still
 * running — resetting its state so the Lockdown purge-failure error can never
 * render — and fire a pointless app-wide query invalidation. Every exit from
 * sign-out is a full page load (redirect on success, reload on failure),
 * which rebuilds all in-memory state from the now-cleared localStorage.
 */
export function clearImpersonationStateForSignOut() {
  clearAllImpersonationLocalState();
}

export type ImpersonateValue = {
  mode: AppMode;
  setMode: (mode: AppMode) => Promise<boolean>;
  round: RoundAggregate;
  underlyingUserId: string;
  underlyingUserIsSfcAdmin: boolean;
  userProfiles: UserProfiles;
};

type ImpersonateContextValue = {
  value: ImpersonateValue | null;
  setValue: (v: ImpersonateValue | null) => void;
};

const ImpersonateContext = createContext<ImpersonateContextValue | null>(null);

export function ImpersonateProvider({ children }: { children: ReactNode }) {
  const [value, setValue] = useState<ImpersonateValue | null>(null);
  const ctx = useMemo(() => ({ value, setValue }), [value]);
  return (
    <ImpersonateContext.Provider value={ctx}>
      {children}
    </ImpersonateContext.Provider>
  );
}

export function useImpersonateContext() {
  const ctx = useContext(ImpersonateContext);
  return ctx ?? { value: null, setValue: () => {} };
}

export function useSetImpersonate() {
  const { setValue } = useImpersonateContext();
  return setValue;
}

function useImpersonateMenu(): {
  show: boolean;
  items: { id: string; label: string; disabled: boolean }[];
  viewAsUser: (userId: string | null) => Promise<void>;
} {
  const { value } = useImpersonateContext();
  const [loading, setLoading] = useState(false);

  if (!value) {
    return { show: false, items: [], viewAsUser: async () => {} };
  }

  const {
    round,
    mode,
    setMode,
    underlyingUserId,
    underlyingUserIsSfcAdmin,
    userProfiles,
  } = value;
  const isAdmin = userIsAdmin(underlyingUserId, {
    round,
    userProfiles,
    effectiveIsSfcAdmin: underlyingUserIsSfcAdmin,
  });
  const realTrack = round.members[underlyingUserId]?.track ?? "NO_TRACK";
  const isRecommender = isUserRecommender(round, underlyingUserId);

  if (
    !isAdmin ||
    realTrack !== "NO_TRACK" ||
    isRecommender ||
    mode.viewAsUserId != null
  ) {
    return { show: false, items: [], viewAsUser: async () => {} };
  }

  const items = Object.entries(userProfiles).map(([key, userProfile]) => ({
    id: key,
    label: `${userProfile.name} (${userProfile.track}, ${userProfile.email})`,
    disabled: key === underlyingUserId,
  }));

  async function viewAsUser(userId: string | null) {
    if (loading) {
      return;
    }

    setLoading(true);
    await sleep(0);
    await setMode({
      ...mode,
      viewAsTrackMember: null,
      viewAsUserId: userId,
    });
    setLoading(false);
  }

  return { show: true, items, viewAsUser };
}

export function useImpersonateDropdown(): {
  show: boolean;
  isImpersonating: boolean;
  viewAsUserId: string | null;
  underlyingUserId: string | null;
  userProfiles: UserProfiles;
  items: {
    id: string;
    label: string;
    initials: string | null;
    disabled: boolean;
  }[];
  viewAsUser: (userId: string | null) => Promise<void>;
} {
  const { value } = useImpersonateContext();
  const [loading, setLoading] = useState(false);
  const globalRef = useImpersonationRefValue();
  const global = useActiveGlobalImpersonation();
  const { data: currentUser } = useCurrentUser();

  // Only fetch for SFC admins — avoids 403s for non-admin users
  const { data: allUsers } = trpc.getImpersonatableUsers.useQuery(undefined, {
    staleTime: 5 * 60 * 1000,
    enabled: currentUser?.isSfcAdmin === true,
    retry: false,
  });

  const empty = {
    show: false,
    isImpersonating: false,
    viewAsUserId: null as string | null,
    underlyingUserId: null as string | null,
    userProfiles: {} as UserProfiles,
    items: [] as {
      id: string;
      label: string;
      initials: string | null;
      disabled: boolean;
    }[],
    viewAsUser: async () => {},
  };

  // Not an SFC admin and not impersonating — nothing to show
  if (!allUsers && !globalRef) {
    return empty;
  }

  const recentIds = readGlobalRecentUserIds();
  const roundMemberIds = value
    ? new Set(Object.keys(value.round.members))
    : null;
  const items = (allUsers ?? [])
    .map((u) => ({
      id: u.id,
      label: `${u.name ?? u.email} (${u.email})`,
      // Round members are known by their initials all over the round UI, so
      // the picker accepts them as a search term while inside a round.
      initials: value?.round.members[u.id]?.initials ?? null,
      disabled: false,
    }))
    .sort((a, b) => {
      // Recent targets first (in recency order)
      const aRecent = recentIds.indexOf(a.id);
      const bRecent = recentIds.indexOf(b.id);

      if (aRecent !== -1 && bRecent !== -1) {
        return aRecent - bRecent;
      }

      if (aRecent !== -1) {
        return -1;
      }

      if (bRecent !== -1) {
        return 1;
      }

      // Then round members (when inside a round)
      if (roundMemberIds) {
        const aInRound = roundMemberIds.has(a.id);
        const bInRound = roundMemberIds.has(b.id);

        if (aInRound && !bInRound) {
          return -1;
        }

        if (!aInRound && bInRound) {
          return 1;
        }
      }

      return 0;
    });

  const userProfiles = Object.fromEntries([
    ...(allUsers ?? []).map((u) => [
      u.id,
      {
        id: u.id,
        name: u.name,
        email: u.email,
        isSfcAdmin: u.isSfcAdmin,
        track: "NO_TRACK" as const,
        deanonymize: false,
      },
    ]),
    // Include the impersonated user's profile from global state so the
    // selector can render before the full user list loads
    ...(global && !allUsers?.some((u) => u.id === global.userId)
      ? [
          [
            global.userId,
            {
              id: global.userId,
              name: global.displayName,
              email: "",
              isSfcAdmin: global.isSfcAdmin ?? false,
              track: "NO_TRACK" as const,
              deanonymize: false,
            },
          ],
        ]
      : []),
  ]) as UserProfiles;

  async function viewAsUser(userId: string | null) {
    if (loading) {
      return;
    }

    setLoading(true);

    try {
      await sleep(0);

      if (value) {
        // Inside a round: go through setMode so round state and global state
        // stay in sync. setMode may refuse (e.g. unconfirmed actions) and
        // leave everything untouched — bail in that case.
        const applied = await value.setMode({
          ...value.mode,
          viewAsTrackMember: null,
          viewAsUserId: userId,
        });

        if (!applied) {
          return;
        }

        // setMode set global with whatever name the round profile had (or
        // cleared it when userId is null). Override with the better display
        // name from the full user list, keeping the admin flag setMode
        // resolved when the user list doesn't have this target.
        if (userId) {
          const user = allUsers?.find((u) => u.id === userId);
          setGlobalImpersonation({
            userId,
            displayName: user?.name ?? user?.email ?? userId,
            isSfcAdmin:
              user?.isSfcAdmin ?? getGlobalImpersonation()?.isSfcAdmin ?? false,
            setByUserId: currentUser?.userId,
          });
          trackGlobalRecentUser(userId);
        }
      } else {
        // Outside a round: update global state directly and notify listeners
        if (userId) {
          const user = allUsers?.find((u) => u.id === userId);
          setGlobalImpersonation({
            userId,
            displayName: user?.name ?? user?.email ?? userId,
            isSfcAdmin: user?.isSfcAdmin ?? false,
            setByUserId: currentUser?.userId,
          });
          trackGlobalRecentUser(userId);
          impersonationRef.current = userId;
          notifyImpersonationListeners();
        } else {
          clearGlobalImpersonation();
        }
      }
    } finally {
      setLoading(false);
    }
  }

  return {
    show: true,
    isImpersonating: globalRef != null,
    viewAsUserId: global?.userId ?? null,
    underlyingUserId: value?.underlyingUserId ?? null,
    userProfiles,
    items,
    viewAsUser,
  };
}

export function useImpersonationBanner(): {
  isImpersonating: boolean;
  viewAsUserId: string | null;
  userProfiles: UserProfiles;
  stopImpersonating: () => Promise<void>;
} {
  const { value } = useImpersonateContext();
  // Same read as the effective user: the banner shows exactly when this tab
  // is acting as someone else.
  const globalEntry = useActiveGlobalImpersonation();

  if (value && value.mode.viewAsUserId != null) {
    const { mode, setMode, userProfiles } = value;
    return {
      isImpersonating: true,
      viewAsUserId: mode.viewAsUserId,
      userProfiles,
      stopImpersonating: async () => {
        await sleep(0);
        // Let setMode manage global state and listeners in one step. If it
        // refuses (e.g. unconfirmed actions), nothing changes and the user
        // sees a toast — avoids leaving global / round state out of sync.
        await setMode({
          ...mode,
          viewAsTrackMember: null,
          viewAsUserId: null,
        });
      },
    };
  }

  // Outside a round (e.g. homepage): fall back to global impersonation state.
  const global = globalEntry;

  if (global) {
    return {
      isImpersonating: true,
      viewAsUserId: global.userId,
      userProfiles: {
        [global.userId]: {
          id: global.userId,
          name: global.displayName,
          email: "",
          isSfcAdmin: global.isSfcAdmin ?? false,
          track: "NO_TRACK" as const,
          deanonymize: false,
        },
      },
      stopImpersonating: async () => {
        clearGlobalImpersonation();
      },
    };
  }

  return {
    isImpersonating: false,
    viewAsUserId: null,
    userProfiles: {} as UserProfiles,
    stopImpersonating: async () => {},
  };
}

/**
 * Round admin / funder flags for chrome (nav, header), keyed to the **acting-as** user
 * (`viewAsUserId` when impersonating), not the underlying logged-in user.
 * While impersonating, the real user's SFC-admin bit is not applied.
 */
export function useRoundAdmin(): {
  isAdmin: boolean;
  isFunder: boolean;
  isRecommender: boolean;
} {
  const { value } = useImpersonateContext();

  if (!value) {
    return { isAdmin: false, isFunder: false, isRecommender: false };
  }

  const {
    round,
    underlyingUserId,
    underlyingUserIsSfcAdmin,
    userProfiles,
    mode,
  } = value;
  const actingAsUserId = mode.viewAsUserId ?? underlyingUserId;
  const isImpersonating = mode.viewAsUserId != null;
  const effectiveIsSfcAdmin = isImpersonating
    ? false
    : underlyingUserIsSfcAdmin;
  return {
    isAdmin: userIsAdmin(actingAsUserId, {
      round,
      userProfiles,
      effectiveIsSfcAdmin,
    }),
    isFunder: isUserFunder(round, actingAsUserId),
    isRecommender: !!isUserRecommender(round, actingAsUserId),
  };
}

export { useImpersonateMenu };
